// TanStack Query hooks over the host notification feed client.
//
// The bell preview and full Inbox have separate list caches: the preview stays
// bounded at 20 while the Inbox owns its filters and cursor. Writes update both
// caches and reconcile the separate unread-total query, so the badge remains
// accurate even when neither visible list contains every unread row.

import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import type { NotificationClass, NotificationItem } from "@playbookai/types";
import type {
  NotificationFeedPage,
  NotificationListParams,
} from "@playbookai/notification-client/browser";
import { retryTransientReadStatus } from "@playbookai/api-clients";
import { useAuthStore } from "../auth/auth-store.js";
import { notificationsApi } from "../api/notifications-client.js";

/**
 * The documented fallback interval, and not a new number.
 *
 * Seven shipped comments in the notification service and its client already
 * name a 60-second poll as the thing that corrects the badge after a dropped
 * push — `fanout.service.ts`, `notification-publisher.ts` and
 * `module-enablement.cache.ts` each fail open on that promise. Until now no
 * client polled at all, so every one of those was resting on a fallback that
 * did not exist.
 *
 * Exported so a test can assert the default without waiting a minute for it.
 */
export const NOTIFICATIONS_POLL_MS = 60_000;
export const NOTIFICATION_PREVIEW_LIMIT = 20;

export interface NotificationsQueryOptions {
  /**
   * Poll interval override. Pass a small number to drive the poll in a test,
   * or `false` to switch polling OFF so a test can prove that something else
   * — a mutation reconciling the count, say — is what moved the number. A
   * behaviour asserted with polling on may only be passing because the poll
   * refetched.
   */
  readonly intervalMs?: number | false;
}

/** Query key, scoped by tenant + user so a tenant/session switch can't read or
   persist another scope's cache (the mock partitions by `tenantId::userId`). */
export function notificationsQueryKey(
  tenantId: string | null | undefined,
  userId: string | undefined,
): readonly unknown[] {
  return ["notifications", tenantId ?? "none", userId ?? "anon"] as const;
}

export function notificationInboxQueryPrefix(
  tenantId: string | null | undefined,
  userId: string | undefined,
): readonly unknown[] {
  return ["notifications", "inbox", tenantId ?? "none", userId ?? "anon"];
}

export interface NotificationInboxFilters {
  readonly unread?: boolean;
  readonly moduleId?: string;
  readonly class?: NotificationClass;
}

export interface NotificationInboxPage extends NotificationFeedPage {
  /** Successful server-response time; cache-only edits preserve this value. */
  readonly responseReceivedAt: string;
}

export function notificationInboxQueryKey(
  tenantId: string | null | undefined,
  userId: string | undefined,
  filters: NotificationInboxFilters,
  cursor: string | undefined,
  limit: number,
): readonly unknown[] {
  return [
    ...notificationInboxQueryPrefix(tenantId, userId),
    filters,
    cursor ?? "first",
    limit,
  ];
}

/** The badge total. Separate key from the feed: different route, different
   cardinality, and the push carries this one but never the list. */
export function unreadCountQueryKey(
  tenantId: string | null | undefined,
  userId: string | undefined,
): readonly unknown[] {
  return [
    "notifications",
    "unread-count",
    tenantId ?? "none",
    userId ?? "anon",
  ] as const;
}

/**
 * Retry policy for both reads.
 *
 * `retryTransientRead` cannot be used directly: it fast-fails by checking
 * `error instanceof PlaybookApiError`, and this client throws
 * `NotificationClientError`, so the check silently never matches and a 401
 * during session renew would be retried three times — every poll tick.
 */
function retryNotificationRead(failureCount: number, error: Error): boolean {
  const status = (error as { status?: unknown }).status;
  return retryTransientReadStatus(
    failureCount,
    typeof status === "number" ? status : undefined,
  );
}

/**
 * Refetch policy shared by both reads.
 *
 * `refetchOnReconnect` covers the browser losing the network. It does NOT cover
 * a dead bell socket on a healthy network — nothing in TanStack Query knows the
 * socket exists — so the interval is what closes that gap, within one tick.
 */
function refetchPolicy(options: NotificationsQueryOptions | undefined) {
  return {
    refetchInterval: options?.intervalMs ?? NOTIFICATIONS_POLL_MS,
    refetchOnReconnect: "always",
    retry: retryNotificationRead,
  } as const;
}

export function useNotificationsQuery(
  options?: NotificationsQueryOptions,
): UseQueryResult<NotificationItem[]> {
  const tenantId = useAuthStore((s) => s.claims?.tenantId);
  const userId = useAuthStore((s) => s.claims?.userId);
  return useQuery({
    queryKey: notificationsQueryKey(tenantId, userId),
    queryFn: () => notificationsApi.list({ limit: NOTIFICATION_PREVIEW_LIMIT }),
    // Only fetch once scope is known — list() throws without an authenticated
    // scope, so running during auth hydration would cause error/retry churn.
    enabled: Boolean(tenantId && userId),
    ...refetchPolicy(options),
  });
}

/** One cursor page for the full Inbox. Search remains local to this page. */
export function useNotificationInboxQuery(
  filters: NotificationInboxFilters,
  cursor: string | undefined,
  limit = 25,
  options?: NotificationsQueryOptions,
): UseQueryResult<NotificationInboxPage> {
  const tenantId = useAuthStore((s) => s.claims?.tenantId);
  const userId = useAuthStore((s) => s.claims?.userId);
  const params: NotificationListParams = {
    limit,
    ...(cursor ? { cursor } : {}),
    ...filters,
  };
  return useQuery({
    queryKey: notificationInboxQueryKey(
      tenantId,
      userId,
      filters,
      cursor,
      limit,
    ),
    queryFn: async () => ({
      ...(await notificationsApi.listPage(params)),
      responseReceivedAt: new Date().toISOString(),
    }),
    placeholderData: keepPreviousData,
    enabled: Boolean(tenantId && userId),
    ...refetchPolicy(options),
  });
}

/**
 * The caller's true unread total.
 *
 * `enabled` is not optional here either, and not covered by the partitioned
 * key: this route goes through the same client and the same auth path, so
 * without the gate it fires during auth hydration, throws, and — with the
 * retry policy above — burns four requests doing it.
 */
export function useUnreadCountQuery(
  options?: NotificationsQueryOptions,
): UseQueryResult<number> {
  const tenantId = useAuthStore((s) => s.claims?.tenantId);
  const userId = useAuthStore((s) => s.claims?.userId);
  return useQuery({
    queryKey: unreadCountQueryKey(tenantId, userId),
    queryFn: async () => (await notificationsApi.unreadCount()).unreadCount,
    enabled: Boolean(tenantId && userId),
    ...refetchPolicy(options),
  });
}

/**
 * Adopt the feed page the server returns from a write.
 *
 * Both write routes answer with the fresh first page precisely so the client
 * can re-render without a second call (`notifications.controller.ts` says so in
 * as many words). Discarding it left the optimistic guess in the cache as the
 * only record of the write, which matters most for mark-all-read: the server
 * clears only rows at or before the render watermark, so rows past it come back
 * still unread — and adopting the response is what makes that residual visible
 * instead of hidden behind a blanket optimistic clear.
 */
function adoptServerFeed(
  queryClient: QueryClient,
  key: readonly unknown[],
  items: NotificationItem[],
): void {
  queryClient.setQueryData<NotificationItem[]>(
    key,
    items.slice(0, NOTIFICATION_PREVIEW_LIMIT),
  );
}

/**
 * The scope a write was STARTED in, carried through the mutation variables.
 *
 * The keys cannot be read off the hook body inside a callback: by the time a
 * slow request settles the component may have re-rendered under a different
 * tenant or user, and the callback would then reconcile the wrong scope's
 * cache. Passing them as variables binds them at the moment of the click.
 */
interface MarkReadVars extends WriteScope {
  readonly id: string;
}

interface MarkAllReadVars extends WriteScope {
  readonly renderedAt: string | undefined;
}

interface WriteScope {
  readonly feedKey: readonly unknown[];
  readonly countKey: readonly unknown[];
  readonly inboxPrefix: readonly unknown[];
}

function updateInboxPages(
  queryClient: QueryClient,
  prefix: readonly unknown[],
  update: (item: NotificationItem) => NotificationItem,
): void {
  queryClient.setQueriesData<NotificationInboxPage>(
    { queryKey: prefix },
    (page) => (page ? { ...page, items: page.items.map(update) } : page),
  );
}

/**
 * Stop an in-flight feed read from landing on top of the write we are about to
 * make.
 *
 * A poll started before the click is already holding a page in which the row is
 * still unread. Without this, that response resolves after the optimistic write
 * — or after the server's own fresh page has been adopted — and puts the stale
 * row back, leaving the feed disagreeing with both the database and the badge.
 * Cancellation is TanStack's documented remedy for exactly this.
 *
 * Deliberately not awaited: the optimistic write has to stay synchronous so the
 * click still feels instant. In the pinned Query version, cancellation reverts
 * synchronously, before our optimistic write. Keep the default revert option:
 * disabling it leaves the query in an error state with a CancelledError.
 */
function cancelStaleFeedRead(
  queryClient: QueryClient,
  feedKey: readonly unknown[],
): void {
  void queryClient.cancelQueries({ queryKey: feedKey });
}

/**
 * Returns `markRead(id)`. The optimistic cache write recomputes the next feed
 * array from the freshest cache (not an object spread-merge — the feed is a
 * list), so the bell badge and the page update immediately; on error we refetch
 * the authoritative feed rather than restoring a stale snapshot.
 */
export interface MarkNotificationReadAction {
  readonly markRead: (id: string) => void;
  readonly isPending: boolean;
  readonly error: Error | null;
  readonly reset: () => void;
}

export function useMarkNotificationReadAction(): MarkNotificationReadAction {
  const queryClient = useQueryClient();
  const tenantId = useAuthStore((s) => s.claims?.tenantId);
  const userId = useAuthStore((s) => s.claims?.userId);
  const key = notificationsQueryKey(tenantId, userId);
  const countKey = unreadCountQueryKey(tenantId, userId);
  const inboxPrefix = notificationInboxQueryPrefix(tenantId, userId);

  // Callbacks live on the MUTATION, not on each `mutate(...)` call. Per-call
  // callbacks are observer-level: a second click replaces the observed
  // mutation and an unmount removes the observer, so the first request's
  // reconciliation is simply dropped — documented behaviour, not a race. Two
  // rapid clicks would leave the server at zero unread and the badge at one.
  const mutation = useMutation<NotificationItem[], Error, MarkReadVars>({
    mutationFn: (vars) => notificationsApi.markRead(vars.id),
    onSuccess: (items, vars) => {
      adoptServerFeed(queryClient, vars.feedKey, items);
      void queryClient.invalidateQueries({ queryKey: vars.inboxPrefix });
    },
    onError: (_error, vars) => {
      void queryClient.invalidateQueries({ queryKey: vars.feedKey });
      void queryClient.invalidateQueries({ queryKey: vars.inboxPrefix });
    },
    // The optimistic count is a guess about a total this page cannot see, so
    // it is always reconciled — on success and on failure alike.
    onSettled: (_data, _error, vars) => {
      void queryClient.invalidateQueries({ queryKey: vars.countKey });
    },
  });

  const markRead = (id: string): void => {
    const scope: WriteScope = { feedKey: key, countKey, inboxPrefix };
    cancelStaleFeedRead(queryClient, scope.feedKey);
    const current = queryClient.getQueryData<NotificationItem[]>(key);
    // GUARDED, not a blind decrement. The popover fires onMarkRead on every row
    // click with no unread check of its own, so clicking an already-read row
    // would otherwise walk the badge down one per click. The feed write below
    // is idempotent for the same reason; the count has to match it.
    const wasUnread = current?.some((n) => n.id === id && n.unread) === true;
    if (current) {
      queryClient.setQueryData<NotificationItem[]>(
        key,
        current.map((n) => (n.id === id ? { ...n, unread: false } : n)),
      );
    }
    updateInboxPages(queryClient, inboxPrefix, (item) =>
      item.id === id ? { ...item, unread: false } : item,
    );
    if (wasUnread) {
      queryClient.setQueryData<number>(countKey, (count) =>
        count === undefined ? count : Math.max(0, count - 1),
      );
    }
    mutation.mutate({ id, ...scope });
  };

  return {
    markRead,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}

export function useMarkNotificationRead(): (id: string) => void {
  return useMarkNotificationReadAction().markRead;
}

/**
 * Returns `markAllRead()` — same optimistic/rollback shape as mark-one, plus the
 * render watermark.
 *
 * The watermark is `dataUpdatedAt`: the moment the feed the user is looking at
 * was fetched. Sending it is what stops "mark all read" swallowing a
 * notification that arrived while they were reading — without it the server
 * falls back to now() and marks read a row that was never on screen.
 *
 * Because the server bounds the write on that watermark and the optimistic
 * write below does not, the two can legitimately disagree: rows past the
 * watermark stay unread server-side. The count invalidation on settle is what
 * surfaces that rather than leaving the badge on a hopeful zero.
 */
export interface MarkAllNotificationsReadAction {
  readonly markAllRead: (renderedAt?: string) => void;
  readonly isPending: boolean;
  readonly error: Error | null;
  readonly reset: () => void;
}

export function useMarkAllNotificationsReadAction(): MarkAllNotificationsReadAction {
  const queryClient = useQueryClient();
  const tenantId = useAuthStore((s) => s.claims?.tenantId);
  const userId = useAuthStore((s) => s.claims?.userId);
  const key = notificationsQueryKey(tenantId, userId);
  const countKey = unreadCountQueryKey(tenantId, userId);
  const inboxPrefix = notificationInboxQueryPrefix(tenantId, userId);

  // Same reasoning as mark-one: hook-level callbacks so an unmount between the
  // click and the response cannot drop the reconciliation.
  const mutation = useMutation<NotificationItem[], Error, MarkAllReadVars>({
    mutationFn: (vars) => notificationsApi.markAllRead(vars.renderedAt),
    onSuccess: (items, vars) => {
      adoptServerFeed(queryClient, vars.feedKey, items);
      void queryClient.invalidateQueries({ queryKey: vars.inboxPrefix });
    },
    onError: (_error, vars) => {
      void queryClient.invalidateQueries({ queryKey: vars.feedKey });
      void queryClient.invalidateQueries({ queryKey: vars.inboxPrefix });
    },
    onSettled: (_data, _error, vars) => {
      void queryClient.invalidateQueries({ queryKey: vars.countKey });
    },
  });

  const markAllRead = (renderedAtOverride?: string): void => {
    const scope: WriteScope = { feedKey: key, countKey, inboxPrefix };
    // READ BEFORE THE OPTIMISTIC WRITE. `setQueryData` below resets
    // `dataUpdatedAt` to now(), so reading it afterwards would hand the server
    // the click time — the exact fallback the watermark exists to avoid, and it
    // would look like it was working.
    const fetchedAt = queryClient.getQueryState(key)?.dataUpdatedAt;
    // Undefined on a cold cache, where there is no rendered list to bound to
    // and the server's now() is the honest answer.
    const renderedAt =
      renderedAtOverride ??
      (fetchedAt ? new Date(fetchedAt).toISOString() : undefined);

    // AFTER the watermark is read, so cancelling cannot disturb dataUpdatedAt.
    cancelStaleFeedRead(queryClient, scope.feedKey);

    const current = queryClient.getQueryData<NotificationItem[]>(key);
    if (current) {
      queryClient.setQueryData<NotificationItem[]>(
        key,
        current.map((n) => ({ ...n, unread: false })),
      );
    }
    updateInboxPages(queryClient, inboxPrefix, (item) => ({
      ...item,
      unread: false,
    }));
    queryClient.setQueryData<number>(countKey, 0);
    mutation.mutate({ renderedAt, ...scope });
  };

  return {
    markAllRead,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}

export function useMarkAllNotificationsRead(): () => void {
  // A real zero-argument function, not `markAllRead` narrowed to one: handed to
  // an `onClick`, the narrowed one took the click event as its watermark
  // (PB-5336). This preview variant always derives it from the feed cache.
  const { markAllRead } = useMarkAllNotificationsReadAction();
  return () => markAllRead();
}
